import OpenAI from "openai";
import Anthropic from "@anthropic-ai/sdk";
import * as Sentry from "@sentry/react";
import type { GeneratedStoryDraft, StructuredSignal } from "../lib/contracts";
import type { StoryClaim } from "../types";
import { parseCandidateStoryResponse } from "./validators";

/**
 * Secure AI Proxy Gateway 🛡️
 *
 * ROLE: This is the ONLY place in the app where AI network calls are managed.
 * In a true production environment, this file has ZERO API keys. It simply
 * fetches a secure endpoint (e.g. Supabase Edge Function).
 */
export const SecureAiProxy = {
  /**
   * Proxies story generation to a secure environment.
   */
  async generateStory(input: any): Promise<GeneratedStoryDraft> {
    console.log("[SecureProxy] Fetching secure story generation...");

    // SIMULATED SECURE PROXY: In prod, this would be:
    // const res = await fetch(`${SUPABASE_URL}/functions/v1/generate-story`, { ... })

    const provider = input.aiSettings?.provider ?? "local";
    if (provider === "openai") return _generateWithOpenAIInternal(input);
    if (provider === "anthropic") return _generateWithAnthropicInternal(input);

    throw new Error(`Unsupported secure provider: ${provider}`);
  },

  /**
   * Proxies structured signal extraction.
   */
  async extractStructuredSignal(text: string): Promise<StructuredSignal> {
    console.log("[SecureProxy] Fetching secure signal extraction...");

    // Real proxy logic:
    // return fetch('/api/ai/extract', { method: 'POST', body: JSON.stringify({ text }) }).then(r => r.json());

    return _extractStructuredSignalInternal(text);
  },

  /**
   * Proxies voice transcription.
   */
  async transcribe(audioBlob: Blob): Promise<string> {
    console.log("[SecureProxy] Fetching secure transcription...");

    return _transcribeInternal(audioBlob);
  },
};

function assertBrowserAiAllowed() {
  const allowBrowserAi = import.meta.env.VITE_ALLOW_BROWSER_AI === "true";

  if (import.meta.env.PROD && !allowBrowserAi) {
    throw new Error(
      "External AI requests from the browser are disabled in production. Route AI through a server-side proxy or set VITE_ALLOW_BROWSER_AI=true only if you intentionally accept browser-side key exposure.",
    );
  }
}

function buildStoryPrompts(input: any) {
  const proofCatalog = (input.artifacts ?? []).map((artifact: any) => ({
    proof_id: artifact.proof_id,
    source_label: artifact.source_label,
    proof_type: artifact.proof_type,
    content_summary: artifact.content_summary,
    evidence_note: artifact.evidence_note,
    source_text: String(artifact.source_text ?? "").slice(0, 800),
  }));

  const correspondenceCatalog = (input.correspondence ?? []).map((item: any) => ({
    dm_id: item.dm_id,
    channel: item.channel,
    subject: item.subject,
    body: String(item.body ?? "").slice(0, 500),
  }));

  const systemPrompt = [
    "You are Monyawn's evidence-linking story engine.",
    "Write a candidate story only from the provided evidence.",
    "Every factual claim must cite a proof_id from the proof catalog when possible.",
    "Return JSON only with this shape:",
    '{"title":"string","summary":"string","markdown":"string","claims":[{"text":"string","citation_proof_id":"proof_id or null","is_verified":true|false}]}',
    "Rules:",
    "- Do not invent employers, dates, skills, or outcomes not present in the input.",
    "- If a claim cannot be tied to a proof_id, set citation_proof_id to null and is_verified to false.",
    "- Prefer concise, specific claims over generic marketing language.",
    "- The markdown must be readable prose, but the claims array must break out the key verifiable assertions.",
  ].join("\n");

  const userPrompt = JSON.stringify(
    {
      candidate: {
        full_name: input.user?.full_name ?? "Candidate",
        current_role: input.user?.current_role ?? "",
        target_role: input.opportunity?.role_title ?? "",
      },
      opportunity: {
        company_name: input.opportunity?.company_name,
        role_title: input.opportunity?.role_title,
        priority: input.opportunity?.priority,
        current_stage: input.opportunity?.current_stage,
        opportunity_source: input.opportunity?.opportunity_source,
      },
      profile: input.profile
        ? {
            skills_summary: input.profile.skills_summary,
            experience_level: input.profile.experience_level,
            domain_strengths: input.profile.domain_strengths,
            declared_gaps: input.profile.declared_gaps,
          }
        : null,
      proof_catalog: proofCatalog,
      correspondence_catalog: correspondenceCatalog,
    },
    null,
    2,
  );

  return { systemPrompt, userPrompt };
}

function normalizeClaims(
  input: any,
  claims: Array<{ text: string; citation_proof_id?: string | null; is_verified?: boolean }>,
): StoryClaim[] {
  const proofIds = new Set((input.artifacts ?? []).map((artifact: any) => artifact.proof_id));

  return claims
    .filter((claim) => claim.text && claim.text.trim().length > 0)
    .map((claim) => {
      const citation_proof_id =
        claim.citation_proof_id && proofIds.has(claim.citation_proof_id)
          ? claim.citation_proof_id
          : undefined;

      return {
        text: claim.text.trim(),
        citation_proof_id,
        is_verified: citation_proof_id ? claim.is_verified !== false : false,
      };
    });
}

/**
 * INTERNAL: These functions handle the actual SDK calls.
 * In a Next.js or Supabase Edge Function environment, this code
 * lives on the SERVER, never reaching the client bundle.
 */
async function _extractStructuredSignalInternal(text: string): Promise<StructuredSignal> {
  assertBrowserAiAllowed();
  const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
  if (!apiKey) throw new Error("OpenAI API key missing for signal extraction.");

  const client = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });

  const systemPrompt = `You are the Monyawn AI wingman. Extract structured JSON data from career signals. 🥱`;

  try {
    const response = await client.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: text },
      ],
      response_format: { type: "json_object" },
    });

    const content = response.choices[0]?.message?.content || "{}";
    return JSON.parse(content) as StructuredSignal;
  } catch (err) {
    Sentry.captureException(err, { tags: { feature: "ai_signal_extraction_internal" } });
    return {
      companyName: "",
      roleTitle: "",
      email: "",
      salaryRange: "",
      location: "",
      keyRequirements: [],
    };
  }
}

async function _transcribeInternal(audioBlob: Blob): Promise<string> {
  assertBrowserAiAllowed();
  const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
  if (!apiKey) throw new Error("OpenAI API key missing for transcription.");

  const client = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });

  // MediaRecorder blobs usually need to be converted to File for Whisper API.
  const file = new File([audioBlob], "dictation.webm", { type: audioBlob.type });

  const transcription = await client.audio.transcriptions.create({
    file,
    model: "whisper-1",
  });

  return transcription.text;
}

async function _generateWithOpenAIInternal(input: any): Promise<GeneratedStoryDraft> {
  assertBrowserAiAllowed();
  if (!input.aiSettings?.openai_api_key) throw new Error("OpenAI API key missing.");
  const client = new OpenAI({
    apiKey: input.aiSettings.openai_api_key,
    dangerouslyAllowBrowser: true,
  });
  const { systemPrompt, userPrompt } = buildStoryPrompts(input);
  try {
    const completion = await client.chat.completions.create({
      model: input.aiSettings.model || "gpt-4o-mini",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      response_format: { type: "json_object" },
    });
    const parsed = parseCandidateStoryResponse(completion.choices[0]?.message?.content ?? "{}");

    return {
      title: parsed.title,
      summary: parsed.summary,
      markdown: parsed.markdown,
      claims: normalizeClaims(input, parsed.claims),
    };
  } catch (err) {
    Sentry.captureException(err, { tags: { feature: "ai_proxy_openai" } });
    throw err;
  }
}

async function _generateWithAnthropicInternal(input: any): Promise<GeneratedStoryDraft> {
  assertBrowserAiAllowed();
  if (!input.aiSettings?.anthropic_api_key) throw new Error("Anthropic API key missing.");
  const client = new Anthropic({ apiKey: input.aiSettings.anthropic_api_key });
  const { systemPrompt, userPrompt } = buildStoryPrompts(input);
  try {
    const completion = await client.messages.create({
      model: input.aiSettings.model || "claude-3-haiku-20240307",
      max_tokens: 2000,
      system: systemPrompt,
      messages: [{ role: "user", content: userPrompt }],
    });
    const firstBlock = completion.content[0];
    const parsed = parseCandidateStoryResponse(
      firstBlock?.type === "text" ? firstBlock.text : "{}",
    );

    return {
      title: parsed.title,
      summary: parsed.summary,
      markdown: parsed.markdown,
      claims: normalizeClaims(input, parsed.claims),
    };
  } catch (err) {
    Sentry.captureException(err, { tags: { feature: "ai_proxy_anthropic" } });
    throw err;
  }
}
